import React from 'react'
import { Tooltip, message } from '@surecompanies/core_components'
import dayjs from 'dayjs'

import {
  BenefitRow,
  BenefitSortKey,
} from '../../../api/reconciliation-v2-api-client'
import {
  ExplicitCreditReason,
  PULL_DAY_NOT_SET,
  PullDayFilter,
} from '../../../api/reconciliation-v2-enums'
import { PullDateCell } from '../PullDate'
import { colors } from '../tokens'
import { PaymentTypeChip } from './pills'
import PrimaryPaymentMethodCell from './PrimaryPaymentMethodCell'

/**
 * Pure/shared helpers for the BenefitsTable subtree — no styled-components.
 * Holds the label/format utilities, column definitions, provider-pill map, the
 * URL filter (de)serialization, and the tiny DateCell render helper.
 */

/** A discrepancy is "real" only past this dollar tolerance. */
export const DISCREPANCY_EPSILON = 0.08

export const INSURED_COL_WIDTH = 198
export const EXPECTED_COL_MIN_WIDTH = 116
export const EMPLOYEE_COL_WIDTH = 220
export const MONTH_COL_WIDTH = 220
export const ZENDESK_COL_WIDTH = 92

export const copyToClipboard = (value: string) => {
  navigator.clipboard
    .writeText(value)
    .then(() => message.success(`Copied ${value}`))
    .catch(() => message.error('Failed to copy'))
}

export const isTerminated = (status: string | null | undefined) =>
  (status ?? '').toLowerCase().includes('terminated')

/**
 * Case-insensitive test/mock company name pattern — mirrors the reconciliation
 * backend's TEST_COMPANY_NAME_REGEX. `test`, `dummy`, `sandbox` match as
 * substrings (to catch glued forms like "ZZ Test Load"); the shorter tokens
 * (`poc`, `demo`, `qa`, `donotuse`) are whole-word-anchored so real names like
 * "Topco" or "Aqua" can't accidentally match.
 */
const TEST_COMPANY_NAME_REGEX =
  /(test|dummy|sandbox)|(^|[^a-z])(poc|demo|qa|donotuse|do not use)([^a-z]|$)/i

/**
 * Whether a company should be hidden from the Company filter — flagged
 * `isTestClient` OR a test/mock-looking name. The shared administered-customers
 * list carries the flag only (often unset), so the name check is what catches
 * most fakes; the backend already keeps these out of the benefit rows.
 */
export const isTestCompany = (company: {
  name: string
  isTestClient?: boolean
}): boolean =>
  company.isTestClient === true || TEST_COMPANY_NAME_REGEX.test(company.name)

export const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

export const buildPeriodKey = (year: number, monthIndex: number) =>
  `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`

/**
 * Minimal shape `buildBenefitLabel` needs — widened from BenefitRow so a
 * BenefitSearchResult (Transaction Details → Adjust Payment deep link) can also
 * be labelled without a parallel helper.
 */
type BenefitLabelSource = Pick<
  BenefitRow,
  'insuredPersons' | 'carrierName' | 'employeeName'
>

/** Primary insured: the 'employee' relationship when present, else first person. */
export const primaryInsured = (row: Pick<BenefitRow, 'insuredPersons'>) =>
  row.insuredPersons?.find((p) => p.relationship === 'employee') ??
  row.insuredPersons?.[0]

export const buildBenefitLabel = (row: BenefitLabelSource) => {
  const insured = primaryInsured(row)
  return (
    [
      insured ? `${insured.firstName} ${insured.lastName}`.trim() : null,
      row.carrierName,
    ]
      .filter(Boolean)
      .join(' · ') || row.employeeName
  )
}

/**
 * Hideable columns — the design's column menu exposes all of these; Insured
 * Person, Expected, months and Zendesk are always visible.
 */
export type HideableColKey =
  | 'employeeName'
  | 'companyName'
  | 'carrierName'
  | 'paymentType'
  | 'pullDate'
  | 'primaryPaymentMethod'
  | 'benefitEffectiveDate'
  | 'benefitEndDate'
  | 'latestTxDate'
  | 'latestEditDate'

export const HIDEABLE_COLUMNS: { key: HideableColKey; label: string }[] = [
  { key: 'employeeName', label: 'Employee' },
  { key: 'companyName', label: 'Company' },
  { key: 'carrierName', label: 'Carrier' },
  { key: 'paymentType', label: 'Payment type' },
  { key: 'pullDate', label: 'Pull date' },
  { key: 'primaryPaymentMethod', label: 'Primary payment method' },
  { key: 'benefitEffectiveDate', label: 'Effective Date' },
  { key: 'benefitEndDate', label: 'End Date' },
  { key: 'latestTxDate', label: 'Latest Transaction' },
  { key: 'latestEditDate', label: 'Latest edit' },
]

// ── provider pills ───────────────────────────────────────────────────────────

export const NEUTRAL_PILL = {
  bg: colors.bg.sunken,
  fg: colors.grey[600],
  border: colors.border.default,
}

/** M = Marqeta (purple), W = WEX (blue); other rails are neutral full-name pills. */
export const PROVIDER_PILL: Record<
  string,
  { label: string; bg: string; fg: string; border: string }
> = {
  marqeta: { label: 'M', ...colors.provider.marqeta },
  wex: { label: 'W', ...colors.provider.wex },
  i_stream: { label: 'iStream', ...NEUTRAL_PILL },
  check: { label: 'Check', ...NEUTRAL_PILL },
  group_ach: { label: 'Group ACH', ...NEUTRAL_PILL },
  ee_paid: { label: 'EE Paid', ...NEUTRAL_PILL },
}

/**
 * Humanize a payment_assignment payment_type / payment_status value for
 * display — 'group_ach' -> 'Group ACH', 'pending' -> 'Pending'. Well-known
 * acronyms stay upper-cased.
 */
// Payment-type words that stay fully upper-cased instead of Title-cased:
// 'ach' = Automated Clearing House, 'eft' = Electronic Funds Transfer,
// 'cc' = Credit Card. These are backend data values (matched below via
// `.has(word.toLowerCase())`), so keep the strings exactly as the API sends
// them — they are lookup keys, not display labels.
export const PAYMENT_LABEL_ACRONYMS = new Set(['ach', 'eft', 'cc'])
export const formatPaymentLabel = (value: string): string =>
  value
    .split(/[_\s]+/)
    .filter((word) => word.length > 0)
    .map((word) =>
      PAYMENT_LABEL_ACRONYMS.has(word.toLowerCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    )
    .join(' ')

/** Whether a given benefit-month falls outside the benefit's coverage window
 *  (before effective / after end), plus the tooltip explaining which. */
export const monthCoverage = (row: BenefitRow, year: number, mi: number) => {
  const periodKey = buildPeriodKey(year, mi)
  const pastEnd =
    !!row.benefitEndDate &&
    dayjs(periodKey).isAfter(dayjs(row.benefitEndDate), 'month')
  const preEffective =
    !!row.benefitEffectiveDate &&
    dayjs(periodKey).isBefore(
      dayjs(row.benefitEffectiveDate).startOf('month'),
      'month'
    )
  const title = pastEnd
    ? `After benefit end (${dayjs(row.benefitEndDate!).format('MMM D, YYYY')})`
    : preEffective
      ? `Before benefit start (${dayjs(row.benefitEffectiveDate!).format('MMM D, YYYY')})`
      : ''
  return { outOfRange: pastEnd || preEffective, title }
}

// ── small render helpers ─────────────────────────────────────────────────────

export const DateCell: React.FC<{ d?: string | null; muted?: boolean }> = ({
  d,
  muted,
}) =>
  d ? (
    <Tooltip title={dayjs(d).format('YYYY-MM-DD HH:mm')}>
      <span style={{ whiteSpace: 'nowrap' }}>
        {dayjs(d).format('MM/DD/YYYY')}
      </span>
    </Tooltip>
  ) : (
    <span style={{ color: muted ? colors.text.disabled : colors.text.muted }}>
      —
    </span>
  )

/** Hideable info columns rendered generically (Employee has bespoke markup). */
export const INFO_COLUMNS: {
  key: Exclude<HideableColKey, 'employeeName'>
  label: string
  sortKey?: BenefitSortKey
  minWidth: number
  nowrap?: boolean
  render: (row: BenefitRow) => React.ReactNode
}[] = [
  {
    key: 'companyName',
    label: 'Company',
    sortKey: BenefitSortKey.CompanyName,
    minWidth: 200,
    render: (r) =>
      r.companyName ?? <span style={{ color: colors.text.disabled }}>—</span>,
  },
  {
    key: 'carrierName',
    label: 'Carrier',
    sortKey: BenefitSortKey.CarrierName,
    minWidth: 170,
    render: (r) =>
      r.carrierName ?? <span style={{ color: colors.text.disabled }}>—</span>,
  },
  {
    key: 'paymentType',
    label: 'Payment type',
    minWidth: 140,
    render: (r) =>
      r.latestPaymentType ? (
        <PaymentTypeChip type={r.latestPaymentType} />
      ) : (
        <span style={{ color: colors.text.disabled }}>—</span>
      ),
  },
  {
    key: 'pullDate',
    label: 'Pull date',
    minWidth: 120,
    nowrap: true,
    render: (r) => <PullDateCell benefitId={r.benefitId} pullDay={r.pullDay} />,
  },
  {
    key: 'primaryPaymentMethod',
    label: 'Primary payment method',
    minWidth: 150,
    render: (r) => <PrimaryPaymentMethodCell row={r} />,
  },
  {
    key: 'benefitEffectiveDate',
    label: 'Effective Date',
    sortKey: BenefitSortKey.BenefitEffectiveDate,
    minWidth: 110,
    nowrap: true,
    render: (r) => <DateCell d={r.benefitEffectiveDate} />,
  },
  {
    key: 'benefitEndDate',
    label: 'End Date',
    minWidth: 110,
    nowrap: true,
    render: (r) => <DateCell d={r.benefitEndDate} />,
  },
  {
    key: 'latestTxDate',
    label: 'Latest Transaction',
    sortKey: BenefitSortKey.LatestTxDate,
    minWidth: 150,
    nowrap: true,
    render: (r) => <DateCell d={r.latestTxDate} />,
  },
  {
    key: 'latestEditDate',
    label: 'Latest edit',
    sortKey: BenefitSortKey.LatestEditDate,
    minWidth: 120,
    nowrap: true,
    render: (r) => <DateCell d={r.latestEditDate} muted />,
  },
]

// ── URL query-string sync (shareable filters) ────────────────────────────────

export const DEFAULT_SORT_BY = BenefitSortKey.InsuredPersons
export const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc'

/** Keys this table owns. Sibling tabs on Payment Reconciliation merge around these. */
export const BENEFITS_URL_KEYS = [
  'q',
  'clientId',
  'carrier',
  'year',
  'emptyInMonth',
  'fullYear',
  'hasIssue',
  'pullDays',
  'hasExplicitCredit',
  'creditReasons',
  'creditNote',
  'sortBy',
  'sortOrder',
  'page',
] as const

/**
 * Deep-link contract for the Transaction Details ledger → Benefits "Adjust
 * Payment" jump. A row on the Transaction Details tab writes these (plus
 * `tab=benefits`); this table reads them once on mount, resolves the benefit,
 * opens the Adjust Payment modal on that coverage-month cell, then clears them.
 * Kept out of BENEFITS_URL_KEYS on purpose — they're one-shot triggers, not
 * persistent filters, and are consumed/cleared explicitly.
 */
export const ADJ_LINK = {
  benefitId: 'adjBenefitId',
  period: 'adjPeriod',
  /** Preselect this transaction's card in the opened modal. */
  txn: 'adjTxn',
} as const

export const ADJ_LINK_KEYS = [
  ADJ_LINK.benefitId,
  ADJ_LINK.period,
  ADJ_LINK.txn,
] as const

export interface UrlFilters {
  search: string
  clientId: string | undefined
  carrier: string | undefined
  year: number
  showFullYear: boolean
  emptyInMonth: string | undefined
  /** Tri-state on purpose: only `true` (errors-only) round-trips via the URL. */
  hasIssue: true | undefined
  /** Empty = no pull-day filter. Comma-joined in the URL. */
  pullDays: PullDayFilter[]
  /** Tri-state: `false` (no explicit credit in `year`) round-trips too. */
  hasExplicitCredit: boolean | undefined
  /** Empty = any reason. Comma-joined in the URL. */
  creditReasons: ExplicitCreditReason[]
  /** Applied "Credit note contains" text, trimmed. */
  creditNote: string
  sortBy: BenefitSortKey
  sortOrder: 'asc' | 'desc'
  page: number
}

export const isBenefitSortKey = (
  value: string | undefined
): value is BenefitSortKey =>
  Object.values(BenefitSortKey).includes(value as BenefitSortKey)

const parsePullDay = (raw: string): PullDayFilter | undefined => {
  if (raw === PULL_DAY_NOT_SET) return PULL_DAY_NOT_SET
  if (!/^\d+$/.test(raw)) return undefined
  const day = Number(raw)
  return day >= 1 && day <= 31 ? day : undefined
}

/**
 * The URL's comma-joined `pullDays`, keeping only values EPS accepts (1–31 and
 * `PULL_DAY_NOT_SET`) so a hand-edited link can't turn the list into a 400.
 * Also takes an array: the router parses a repeated key (`?pullDays=1&pullDays=2`)
 * into one, whatever the query's declared type says.
 */
const parsePullDays = (raw: string | string[] | undefined): PullDayFilter[] => {
  const joined = Array.isArray(raw) ? raw.join(',') : (raw ?? '')
  const days: PullDayFilter[] = []
  for (const part of joined.split(',')) {
    const day = parsePullDay(part.trim())
    if (day !== undefined && !days.includes(day)) days.push(day)
  }
  return days
}

/** `pullDays` for the URL; undefined when nothing is selected so the key drops. */
export const serializePullDays = (days: PullDayFilter[]): string | undefined =>
  days.length > 0 ? days.join(',') : undefined

/** Backend-enforced bound on the grid's `noteSearch`. */
export const CREDIT_NOTE_MAX_LENGTH = 200

const isExplicitCreditReason = (value: string): value is ExplicitCreditReason =>
  Object.values(ExplicitCreditReason).includes(value as ExplicitCreditReason)

/**
 * The URL's comma-joined `creditReasons`, keeping only reasons EPS accepts so
 * a hand-edited link can't turn the list into a 400. Also takes an array, for
 * a repeated key — same as `parsePullDays`.
 */
const parseCreditReasons = (
  raw: string | string[] | undefined
): ExplicitCreditReason[] => {
  const joined = Array.isArray(raw) ? raw.join(',') : (raw ?? '')
  const reasons: ExplicitCreditReason[] = []
  for (const part of joined.split(',')) {
    const reason = part.trim()
    if (isExplicitCreditReason(reason) && !reasons.includes(reason)) {
      reasons.push(reason)
    }
  }
  return reasons
}

/** `creditReasons` for the URL; undefined when nothing is selected so the key drops. */
export const serializeCreditReasons = (
  reasons: ExplicitCreditReason[]
): string | undefined => (reasons.length > 0 ? reasons.join(',') : undefined)

const parseHasCredit = (raw: string | undefined): boolean | undefined => {
  if (raw === 'true') return true
  if (raw === 'false') return false
  return undefined
}

/**
 * The list query's explicit credit params. A reason or note text narrows "has
 * explicit credit" and is sent only under it, so a hidden leftover can never
 * filter the grid. "No explicit credit" stands alone; EPS rejects it combined
 * with a reason or note text.
 */
export const buildCreditFilterParams = (
  hasExplicitCredit: boolean | undefined,
  creditReasons: ExplicitCreditReason[],
  creditNote: string
): {
  hasExplicitCredit: boolean | undefined
  creditReasons: ExplicitCreditReason[] | undefined
  noteSearch: string | undefined
} => {
  if (hasExplicitCredit !== true) {
    return {
      hasExplicitCredit,
      creditReasons: undefined,
      noteSearch: undefined,
    }
  }
  const note = creditNote.trim()
  return {
    hasExplicitCredit,
    creditReasons: creditReasons.length > 0 ? creditReasons : undefined,
    noteSearch: note.length > 0 ? note : undefined,
  }
}

/**
 * Whether any explicit credit control is set. The three controls narrow one
 * credit, so the Filters badge counts them as a single filter.
 */
export const isCreditFilterActive = (
  hasExplicitCredit: boolean | undefined,
  creditReasons: ExplicitCreditReason[],
  creditNote: string
): boolean =>
  hasExplicitCredit !== undefined ||
  creditReasons.length > 0 ||
  creditNote.trim().length > 0

/** Parse the incoming query string into filter state, falling back to the
 *  component defaults so a bare URL behaves exactly as before. */
export const readInitialFilters = (
  query: Record<string, string | undefined>
): UrlFilters => {
  const parsedYear = parseInt(query.year ?? '', 10)
  const parsedPage = parseInt(query.page ?? '', 10)
  const parsedHasCredit = parseHasCredit(query.hasExplicitCredit)
  const noCredit = parsedHasCredit === false
  const creditReasons = noCredit ? [] : parseCreditReasons(query.creditReasons)
  const creditNote = noCredit
    ? ''
    : (query.creditNote ?? '').slice(0, CREDIT_NOTE_MAX_LENGTH).trim()
  // A reason or note only narrows "has explicit credit", and the drawer shows
  // them only under it, so a link carrying one without it means has-credit.
  const hasExplicitCredit =
    parsedHasCredit === undefined &&
    (creditReasons.length > 0 || creditNote.length > 0)
      ? true
      : parsedHasCredit
  return {
    search: query.q ?? '',
    clientId: query.clientId || undefined,
    carrier: query.carrier || undefined,
    year: Number.isNaN(parsedYear) ? new Date().getFullYear() : parsedYear,
    showFullYear: query.fullYear === 'true',
    emptyInMonth: query.emptyInMonth || undefined,
    hasIssue: query.hasIssue === 'true' ? true : undefined,
    pullDays: parsePullDays(query.pullDays),
    hasExplicitCredit,
    creditReasons,
    creditNote,
    sortBy: isBenefitSortKey(query.sortBy) ? query.sortBy : DEFAULT_SORT_BY,
    sortOrder:
      query.sortOrder === 'asc' || query.sortOrder === 'desc'
        ? query.sortOrder
        : DEFAULT_SORT_ORDER,
    page: Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage,
  }
}
